<script setup lang="ts">
const {
  permission,
  isSupported,
  canSendNotifications,
  isSubscribed,
  notifications,
  unreadCount,
  requestPermission,
  subscribe,
  unsubscribe,
  markAllAsRead,
  clearAllNotifications,
  checkExistingSubscription
} = useNotifications()

const { t } = useI18n()
const userSettingsStore = useUserSettingsStore()

const requestingPermission = ref(false)
const subscribing = ref(false)
const unsubscribing = ref(false)

// Navigation provider setting
const navigationProvider = ref(
  userSettingsStore.getUserSettings('navigation_provider_setting') || 'google'
)

const navigationProviderOptions = computed(() => [
  { label: t('components.settingsPanel.googleMaps'), value: 'google' },
  { label: t('components.settingsPanel.appleMaps'), value: 'apple' }
])

const showWeekends = ref(userSettingsStore.getUserSettings('calendar_show_weekends'))

const gptSuggestionsEnabled = ref(
  userSettingsStore.getUserSettings('gpt_suggestions_enabled') || false
)

// Get VAPID public key from environment or config
const vapidPublicKey = ref(
  'BOysiemR2WI9Wmy4_F0SbKXxAorFC0Ga90I5jmiybhHmLdFztNKlYLsnD7gNGRpZIh0HAbPvxl3ezAE_0TW_jB0'
) // Replace with actual key

const subscriptionStatus = computed(() => {
  if (isSubscribed.value) {
    return 'subscribedToPushNotifications'
  }
  return 'subscribeToPushNotifications'
})

onMounted(async () => {
  await checkExistingSubscription()
})

const requestNotificationPermission = async () => {
  try {
    requestingPermission.value = true
    await requestPermission()
  } catch {
  } finally {
    requestingPermission.value = false
  }
}

const subscribeToNotifications = async () => {
  try {
    subscribing.value = true
    await subscribe(vapidPublicKey.value)
  } catch {
  } finally {
    subscribing.value = false
  }
}

const unsubscribeFromNotifications = async () => {
  try {
    unsubscribing.value = true
    await unsubscribe()
  } catch {
  } finally {
    unsubscribing.value = false
  }
}

const updateNavigationProvider = (value: string) => {
  navigationProvider.value = value
  userSettingsStore.updateUserSettings('navigation_provider_setting', value)
}

const updateShowWeekends = (value: boolean) => {
  showWeekends.value = value
  userSettingsStore.updateUserSettings('calendar_show_weekends', value)
}

const updateGptSuggestionsEnabled = (value: boolean) => {
  gptSuggestionsEnabled.value = value
  userSettingsStore.updateUserSettings('gpt_suggestions_enabled', value)
}
</script>

<template>
  <div class="notification-panel">
    <!-- Calendar Settings -->
    <div class="p-4 border-2 border-primary rounded-lg mb-4">
      <h3 class="text-lg font-medium text-blue-900 mb-2">
        {{ $t('pages.calendar.settings') }}
      </h3>
      <div class="flex items-center gap-2">
        <Checkbox
          :model-value="showWeekends"
          :binary="true"
          input-id="show-weekends-settings"
          @update:model-value="updateShowWeekends"
        />
        <label for="show-weekends-settings" class="text-sm">
          {{ $t('pages.calendar.showWeekend') }}
        </label>
      </div>
    </div>

    <!-- ChatGPT Settings -->
    <div class="p-4 border-2 border-primary rounded-lg mb-4">
      <h3 class="text-lg font-medium text-blue-900 mb-2">
        {{ $t('components.settingsPanel.gptSuggestions') }}
      </h3>
      <p class="mb-3">{{ $t('components.settingsPanel.gptSuggestionsDescription') }}</p>
      <div class="flex items-center gap-2">
        <Checkbox
          :model-value="gptSuggestionsEnabled"
          :binary="true"
          input-id="gpt-suggestions-enabled"
          @update:model-value="updateGptSuggestionsEnabled"
        />
        <label for="gpt-suggestions-enabled" class="text-sm">
          {{ $t('components.settingsPanel.gptSuggestions') }}
        </label>
      </div>
    </div>

    <div class="p-4 border-2 border-primary rounded-lg mb-4">
      <h3 class="text-lg font-medium text-blue-900 mb-2">
        {{ $t('components.settingsPanel.navigationProvider') }}
      </h3>
      <p class="mb-3">{{ $t('components.settingsPanel.navigationProviderDescription') }}</p>
      <Select
        :model-value="navigationProvider"
        :options="navigationProviderOptions"
        option-label="label"
        option-value="value"
        :placeholder="$t('components.settingsPanel.navigationProviderDescription')"
        class="w-full"
        @update:model-value="updateNavigationProvider"
      />
    </div>

    <div v-if="isSupported">
      <div v-if="permission !== 'granted'" class="p-4 border-2 border-primary rounded-lg mb-4">
        <h3 class="text-lg font-medium text-blue-900 mb-2">
          {{ $t('components.settingsPanel.grantAuthorization') }}
        </h3>
        <p class="mb-3">{{ $t('components.settingsPanel.grantAuthorizationDetail') }}</p>
        <Button
          :loading="requestingPermission"
          :label="$t('components.settingsPanel.enableNotifications')"
          icon="pi pi-bell"
          icon-class="text-white"
          class="p-button-primary"
          @click="requestNotificationPermission"
        />
      </div>

      <div v-if="canSendNotifications" class="p-4 border-2 border-primary rounded-lg mb-4">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-lg font-medium">
              {{ $t('components.settingsPanel.subscription') }}
            </h3>
            <p class="text-green-700 mt-1">
              {{ $t('components.settingsPanel.' + subscriptionStatus) }}
            </p>
          </div>
          <Button
            v-if="!isSubscribed"
            :loading="subscribing"
            :label="$t('components.settingsPanel.subscribe')"
            severy="primary"
            class="min-w-fit"
            @click="subscribeToNotifications"
          />
          <Button
            v-else
            :loading="unsubscribing"
            :label="$t('components.settingsPanel.unsubscribe')"
            severity="danger"
            class="min-w-fit"
            @click="unsubscribeFromNotifications"
          />
        </div>
      </div>

      <div v-if="notifications.length > 0" class="notification-list">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium">
            {{ $t('components.settingsPanel.notifications') }}
            <Badge v-if="unreadCount > 0" :value="unreadCount" severity="danger" />
          </h3>
          <div class="flex gap-2">
            <Button
              v-if="unreadCount > 0"
              :label="$t('components.settingsPanel.markAllRead')"
              size="small"
              text
              @click="markAllAsRead"
            />
            <Button
              :label="$t('components.settingsPanel.clearAll')"
              size="small"
              text
              severity="danger"
              @click="clearAllNotifications"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notification-panel {
  max-width: 600px;
}

.notification-item:hover {
  @apply bg-gray-100 border-gray-300;
}

.notification-item.bg-blue-50:hover {
  @apply bg-blue-100 border-blue-300;
}
</style>
